/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as UnsavedChangesActionCreators from '@app/actions/UnsavedChangesActionCreators';
import * as UserSettingsActionCreators from '@app/actions/UserSettingsActionCreators';
import {Select} from '@app/components/form/Select';
import {SettingsTabSection} from '@app/components/modals/shared/SettingsTabLayout';
import UserSettingsStore from '@app/stores/UserSettingsStore';
import {SensitiveMediaFilterLevel} from '@fluxer/constants/src/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const SENSITIVE_CONTENT_TAB_ID = 'privacy_safety';

export const SensitiveContentTabContent: React.FC = observer(() => {
	const {t} = useLingui();

	const [friendDmFilter, setFriendDmFilter] = useState(UserSettingsStore.sensitiveContentFriendDmFilter);
	const [nonFriendDmFilter, setNonFriendDmFilter] = useState(UserSettingsStore.sensitiveContentNonFriendDmFilter);
	const [guildFilter, setGuildFilter] = useState(UserSettingsStore.sensitiveContentGuildFilter);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const hasUnsavedChanges =
		friendDmFilter !== UserSettingsStore.sensitiveContentFriendDmFilter ||
		nonFriendDmFilter !== UserSettingsStore.sensitiveContentNonFriendDmFilter ||
		guildFilter !== UserSettingsStore.sensitiveContentGuildFilter;

	const handleReset = useCallback(() => {
		setFriendDmFilter(UserSettingsStore.sensitiveContentFriendDmFilter);
		setNonFriendDmFilter(UserSettingsStore.sensitiveContentNonFriendDmFilter);
		setGuildFilter(UserSettingsStore.sensitiveContentGuildFilter);
	}, []);

	const handleSave = useCallback(async () => {
		setIsSubmitting(true);
		try {
			await UserSettingsActionCreators.update({
				sensitiveContentFriendDmFilter: friendDmFilter,
				sensitiveContentNonFriendDmFilter: nonFriendDmFilter,
				sensitiveContentGuildFilter: guildFilter,
			});
		} finally {
			setIsSubmitting(false);
		}
	}, [friendDmFilter, nonFriendDmFilter, guildFilter]);

	useEffect(() => {
		UnsavedChangesActionCreators.setUnsavedChanges(SENSITIVE_CONTENT_TAB_ID, hasUnsavedChanges);
	}, [hasUnsavedChanges]);

	useEffect(() => {
		UnsavedChangesActionCreators.setTabData(SENSITIVE_CONTENT_TAB_ID, {
			onReset: handleReset,
			onSave: handleSave,
			isSubmitting,
		});
	}, [handleReset, handleSave, isSubmitting]);

	useEffect(() => {
		return () => {
			UnsavedChangesActionCreators.clearUnsavedChanges(SENSITIVE_CONTENT_TAB_ID);
		};
	}, []);

	const filterOptions = useMemo(
		() => [
			{value: SensitiveMediaFilterLevel.SHOW, label: t`Show`},
			{value: SensitiveMediaFilterLevel.BLUR, label: t`Blur`},
			{value: SensitiveMediaFilterLevel.BLOCK, label: t`Block`},
		],
		[t],
	);

	const guildFilterOptions = useMemo(
		() => [
			{value: SensitiveMediaFilterLevel.SHOW, label: t`Show`},
			{value: SensitiveMediaFilterLevel.BLUR, label: t`Blur`},
		],
		[t],
	);

	return (
		<SettingsTabSection title={t`Sensitive Content`}>
			<Select<number>
				label={t`Direct messages from friends`}
				value={friendDmFilter}
				options={filterOptions}
				onChange={setFriendDmFilter}
			/>
			<Select<number>
				label={t`Direct messages from others`}
				value={nonFriendDmFilter}
				options={filterOptions}
				onChange={setNonFriendDmFilter}
			/>
			<Select<number>
				label={t`Messages in community channels`}
				value={guildFilter}
				options={guildFilterOptions}
				onChange={setGuildFilter}
			/>
		</SettingsTabSection>
	);
});
